<script setup>
import { ref, inject, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import powerAgentsApi from 'dashboard/api/powerAgents';
import PromptEditor from '../../agentShared/PromptEditor.vue';
import SectionShell from './SectionShell.vue';
import { useRascunhoDaSecao } from '../useRascunhoDaSecao';

const props = defineProps({
  agent: { type: Object, required: true },
});

const { t } = useI18n();
const ctx = inject('powerAgentContext');

const systemPrompt = ref(props.agent.system_prompt || '');
const saving = ref(false);
const showFullscreen = ref(false);

const apiBasePath = computed(
  () => `/api/v1/accounts/${ctx.accountId()}/power_agents/${ctx.agentId()}`
);

// O que a pessoa edita aqui; a página avisa antes de perder (ver useRascunhoDaSecao).
const { suja, limpar, definirSalvar } = useRascunhoDaSecao(() => ({
  systemPrompt: systemPrompt.value,
}));

async function save() {
  saving.value = true;
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        system_prompt: systemPrompt.value,
      }
    );
    ctx.onSectionSaved(updated);
    useAlert(t('POWER_AGENTS.SAVED'));
    limpar();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
}
definirSalvar(save);

// Restaurar grava no servidor: o texto restaurado já é o salvo.
function onVersionRestored(version) {
  systemPrompt.value = version.system_prompt || '';
  limpar();
}
</script>

<template>
  <SectionShell
    :title="t('POWER_AGENTS.PROMPT.TITLE')"
    :description="t('POWER_AGENTS.PROMPT.DESCRIPTION')"
    :saving="saving"
    :suja="suja"
    @save="save"
  >
    <!-- `inline` é obrigatório: sem ele o editor monta em modo tela cheia e cobre a página
         inteira. O i18n-prefix é o que faz os rótulos da barra resolverem. -->
    <div data-busca="prompt">
      <PromptEditor
        v-model="systemPrompt"
        inline
        :api-base-path="apiBasePath"
        :account-id="ctx.accountId()"
        :agent-id="ctx.agentId()"
        :agent-name="ctx.agentName?.()"
        i18n-prefix="POWER_AGENTS.PROMPT"
        @version-restored="onVersionRestored"
        @expand="showFullscreen = true"
      />
    </div>

    <!-- Fullscreen editor: same v-model, so edits sync back to the inline view. -->
    <PromptEditor
      v-if="showFullscreen"
      v-model="systemPrompt"
      :api-base-path="apiBasePath"
      :account-id="ctx.accountId()"
      :agent-id="ctx.agentId()"
      :agent-name="ctx.agentName?.()"
      i18n-prefix="POWER_AGENTS.PROMPT"
      @version-restored="onVersionRestored"
      @close="showFullscreen = false"
    />
  </SectionShell>
</template>
